import React from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, DollarSign, Star, MapPin } from 'lucide-react';

const COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#8b5cf6'];
const STATUS_LABELS_DE = {
  open: 'Offen', bidding: 'Angebote', booked: 'Gebucht', completed: 'Abgeschlossen', cancelled: 'Storniert',
};

export function AdminAnalytics({ analytics }) {
  if (!analytics) return null;

  const statusData = Object.entries(analytics.status_distribution || {}).map(([key, val]) => ({
    name: STATUS_LABELS_DE[key] || key,
    value: val,
  }));

  const timelineData = (analytics.timeline || []).map(d => ({
    ...d,
    date: d.date?.slice(5) || d.date,
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
          <DollarSign size={20} className="opacity-70 mb-2" />
          <p className="text-2xl font-bold">&euro;{analytics.total_revenue?.toLocaleString()}</p>
          <p className="text-sm opacity-80">Gesamtumsatz</p>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <TrendingUp size={20} className="opacity-70 mb-2" />
          <p className="text-2xl font-bold">&euro;{analytics.avg_booking_value?.toFixed(0)}</p>
          <p className="text-sm opacity-80">Durchschn. Buchungswert</p>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white">
          <Star size={20} className="opacity-70 mb-2" />
          <p className="text-2xl font-bold">{analytics.top_drivers?.length || 0}</p>
          <p className="text-sm opacity-80">Aktive Fahrer</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <MapPin size={20} className="opacity-70 mb-2" />
          <p className="text-2xl font-bold">{analytics.timeline?.reduce((s, d) => s + d.bookings, 0) || 0}</p>
          <p className="text-sm opacity-80">Transfers gesamt</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Bookings Over Time */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="font-bold text-sm text-slate-800 mb-4">Buchungen über Zeit</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Bar dataKey="bookings" name="Buchungen" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Revenue Over Time */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="font-bold text-sm text-slate-800 mb-4">Umsatz-Entwicklung</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={v => `€${v}`} />
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }} formatter={v => [`€${v}`, 'Umsatz']} />
              <Line type="monotone" dataKey="revenue" name="Umsatz" stroke="#10b981" strokeWidth={2.5} dot={{ fill: '#10b981', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="font-bold text-sm text-slate-800 mb-4">Transfer-Status Verteilung</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {statusData.map((_, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Top Drivers */}
        <div className="bg-white rounded-xl border border-slate-100 p-5">
          <h3 className="font-bold text-sm text-slate-800 mb-4">Top-Fahrer</h3>
          <div className="space-y-3">
            {analytics.top_drivers?.slice(0, 5).map((driver, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                    idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-orange-600' : 'bg-slate-300'
                  }`}>
                    {idx + 1}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{driver.name}</p>
                    <p className="text-xs text-slate-400">{driver.vehicle} | {driver.region}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    <Star size={14} className="text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-bold text-slate-800">{driver.rating || '—'}</span>
                  </div>
                  <p className="text-xs text-slate-400">{driver.trips} Fahrten</p>
                </div>
              </div>
            ))}
            {(!analytics.top_drivers || analytics.top_drivers.length === 0) && (
              <p className="text-sm text-slate-400 text-center py-4">Noch keine Daten</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
